import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Crumbs, useKeepStore } from "@/components/layout/Crumbs";
import api from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { useShop } from "@/lib/useShop";
import { themeOf } from "@/lib/mall";
import { SHIRT_KINDS, fromPrice } from "@/lib/shirtKinds";
import { GroupCard, LandingHead } from "@/components/shop/GroupCard";
import { Fold } from "@/components/layout/Fold";
import { PageBanner } from "@/components/mall/PageBanner";

const money = (n) => `$${Number(n || 0).toFixed(n % 1 ? 2 : 0)}`;

/** SHIRTS landing (Phase 10): one card per sub-category this shop sells, then DTF. A card opens the garment grid. */
export default function ShirtsLanding() {
    const { store: activeStore } = useMall();
    const { shopSlug } = useShop();
    const keep = useKeepStore();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!shopSlug) return;
        setLoading(true);
        api.get("/catalog/shirts", { params: { store: shopSlug } })
            .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, [shopSlug]);

    if (loading && !data) return <Layout><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;

    const theme = themeOf(activeStore);
    const products = data?.products || [];
    const cards = SHIRT_KINDS.map((k) => ({ ...k, rows: products.filter(k.match) })).filter((k) => k.rows.length);
    const q = shopSlug ? `?store=${shopSlug}` : "";
    const dtf = data?.dtf;

    const cardOf = (k) => {
        const first = k.rows[0];
        const prices = k.rows.map(fromPrice).filter(Boolean);
        const sameDay = k.rows.some((p) => p.ready_in === "same_day");
        const tintable = first.tintable && (first.colors?.[0]?.hex || "").toLowerCase() !== "#ffffff";
        return {
            to: keep(`/shirts/${k.key}`), title: k.label, theme, testid: `shirt-kind-${k.key}`, idPrefix: `shirt-kind-${k.key}`,
            image: first.photo_override || first.mockups?.front?.url, contain: !first.photo_override, tintHex: tintable && !first.photo_override ? first.colors?.[0]?.hex : null,
            hook: `${k.rows.length} style${k.rows.length === 1 ? "" : "s"}${prices.length ? ` · from ${money(Math.min(...prices))} each` : ""}`,
            badge: sameDay ? "Same day" : null,
        };
    };

    return (
        <Layout title="Shirts">
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="shirts-landing">
                <Crumbs trail={[{ label: "Shirts" }]} />
                <div className="mt-4">
                    <LandingHead kicker={activeStore ? `Printed by ${activeStore.name}` : "Shirts"} title="Shirts, tanks & hoodies."
                        line="Pick a shelf. Then pick the garment and design it on screen — the price updates as you go." testid="shirts-head" />
                </div>
                {!cards.length && !dtf?.show ? (
                    <p className="mt-8 text-zinc-600" data-testid="shirts-landing-empty">This shop has no apparel on the shelf yet.</p>
                ) : (
                    <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="shirt-kinds">
                        {cards.map((k) => <GroupCard key={k.key} {...cardOf(k)} />)}
                        {dtf?.show && (
                            <>
                                <GroupCard to={`/shirts/dtf/gang-sheet${q}`} title="DTF Gang Sheet" theme={theme} testid="shirt-kind-gangsheet" idPrefix="shirt-kind-gangsheet"
                                    image={dtf.kind?.image_url} hook={`${dtf.config?.width_inches || 16.5}" wide · ${money(dtf.config?.price_per_linear_foot || 5)} a foot`}
                                    badge={dtf.ready_in === "same_day" ? "Same day" : null} />
                                <GroupCard to={`/order/transfers${q}`} title="DTF Transfers" theme={theme} testid="shirt-kind-transfers" idPrefix="shirt-kind-transfers"
                                    image="/assets/art/frenchie.webp" hook="Your size, your count · press them yourself" />
                            </>
                        )}
                    </div>
                )}
            </section>
            <Fold title="How shirt pricing and turnaround work" kicker="The fine print" testid="shirts-fold-about">
                <PageBanner room="apparel" />
            </Fold>
        </Layout>
    );
}
